/**
 * Themed form controls for the Workshop Log modal (Decisions / Actions /
 * Assumptions). They replace the native `<select>` and
 * `<input type="date">`, whose *open* surfaces (the option list and the
 * calendar) render in raw OS chrome — a light popup in the middle of a
 * dark console, with a washed-out native calendar glyph. These build the
 * open surfaces on the same Radix primitives the rest of the app uses
 * (`@/components/ui/select`, `@/components/ui/popover`), styled with the
 * console tokens so they read as panels in both dark and light.
 *
 * The closed triggers reuse `FIELD_CLASS` so they sit flush with the
 * text inputs in the same row; the popover chrome lives in `index.css`
 * (`.log-pop`, `.log-status-*`, `.log-cal-*`).
 */

import { useEffect, useId, useMemo, useState, type KeyboardEvent } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  FIELD_CLASS,
  daysInMonth,
  formatDueDate,
  leadingBlanks,
  parseISO,
  sameDay,
  toISO,
} from "./log-controls.utils";

/* ── Status dropdown ──────────────────────────────────────────────── */

type StatusSelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  options: readonly string[];
  ariaLabel: string;
  /** Maps a status to its accent colour so the trigger and each option
   *  carry the same meaning the row text does. */
  accentFor: (status: string) => string;
  className?: string;
};

/** Drop-in for the native status `<select>`. The trigger looks like the
 *  field it replaces; the list portals into console-styled chrome. */
export function StatusSelect({
  value,
  onValueChange,
  options,
  ariaLabel,
  accentFor,
  className,
}: StatusSelectProps) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange}>
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(FIELD_CLASS, "text-xs", "log-status-trigger", className)}
        style={{ color: accentFor(value) }}
      >
        <SelectPrimitive.Value />
        <SelectPrimitive.Icon asChild>
          <ChevronDown aria-hidden style={{ width: 13, height: 13, opacity: 0.7 }} />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className="log-pop log-status-content"
        >
          <SelectPrimitive.Viewport>
            {options.map((opt) => (
              <SelectPrimitive.Item
                key={opt}
                value={opt}
                className="log-status-item"
                style={{ color: accentFor(opt) }}
              >
                <SelectPrimitive.ItemText>{opt}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator asChild>
                  <Check aria-hidden style={{ width: 13, height: 13 }} strokeWidth={2.5} />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

/* ── Due-date picker ──────────────────────────────────────────────── */

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const;

type DueDatePickerProps = {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
};

/** Drop-in for `<input type="date">`. The trigger matches the row's
 *  fields; the calendar is a console-styled popover (dark + light). */
export function DueDatePicker({ value, onChange, ariaLabel, className }: DueDatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parseISO(value);
  const today = new Date();

  // The visible month. Re-anchored to the selection (or today) each time
  // the calendar opens, so reopening always lands on the relevant month.
  const [view, setView] = useState(() => selected ?? today);
  useEffect(() => {
    if (open) setView(selected ?? new Date());
    // Only re-anchor on open; `value` changes close the popover anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const monthDays = daysInMonth(year, month);
  const blanks = leadingBlanks(year, month); // Mon-first

  function pick(day: number): void {
    onChange(toISO(year, month, day));
    setOpen(false);
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          data-empty={value ? undefined : ""}
          className={cn(FIELD_CLASS, "text-xs", "log-date-trigger", className)}
        >
          <CalendarDays aria-hidden style={{ width: 13, height: 13, opacity: 0.7, flex: "none" }} />
          <span className="log-date-value">{value ? formatDueDate(value) : "Due date"}</span>
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content align="start" sideOffset={6} className="log-pop log-cal">
          <div className="log-cal-head">
            <button
              type="button"
              className="log-cal-nav"
              aria-label="Previous month"
              onClick={() => setView(new Date(year, month - 1, 1))}
            >
              <ChevronLeft aria-hidden style={{ width: 14, height: 14 }} />
            </button>
            <span className="log-cal-month">
              {view.toLocaleDateString(undefined, { month: "short", year: "numeric" })}
            </span>
            <button
              type="button"
              className="log-cal-nav"
              aria-label="Next month"
              onClick={() => setView(new Date(year, month + 1, 1))}
            >
              <ChevronRight aria-hidden style={{ width: 14, height: 14 }} />
            </button>
          </div>

          <div className="log-cal-grid" role="grid">
            {WEEKDAYS.map((w) => (
              <span key={w} className="log-cal-weekday" aria-hidden>
                {w}
              </span>
            ))}
            {Array.from({ length: blanks }).map((_, i) => (
              <span key={`blank-${i}`} className="log-cal-blank" aria-hidden />
            ))}
            {Array.from({ length: monthDays }).map((_, i) => {
              const day = i + 1;
              const date = new Date(year, month, day);
              const isSelected = selected != null && sameDay(selected, date);
              const isToday = sameDay(today, date);
              return (
                <button
                  key={day}
                  type="button"
                  className={cn(
                    "log-cal-day",
                    isSelected && "is-selected",
                    isToday && "is-today",
                  )}
                  aria-pressed={isSelected || undefined}
                  aria-current={isToday ? "date" : undefined}
                  aria-label={date.toLocaleDateString(undefined, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                  onClick={() => pick(day)}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="log-cal-foot">
            <button
              type="button"
              className="log-cal-action"
              onClick={() => {
                const n = new Date();
                onChange(toISO(n.getFullYear(), n.getMonth(), n.getDate()));
                setOpen(false);
              }}
            >
              Today
            </button>
            <button
              type="button"
              className="log-cal-action"
              data-disabled={value ? undefined : ""}
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              Clear
            </button>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/* ── Owner combobox ───────────────────────────────────────────────── */

type OwnerComboboxProps = {
  value: string;
  onChange: (value: string) => void;
  /** Roster suggestions (display names, deduped + sorted). */
  options: readonly string[];
  ariaLabel: string;
  placeholder?: string;
  className?: string;
};

/** Drop-in for the owner `<input list=…>` + `<datalist>`. The field stays
 *  free-text (type a non-roster owner) but suggests known crew through a
 *  console-styled popover instead of the un-themeable native datalist —
 *  the typeahead pattern mirrors `ReferencePicker`, the app's other
 *  suggestion list. */
export function OwnerCombobox({
  value,
  onChange,
  options,
  ariaLabel,
  placeholder = "Owner",
  className,
}: OwnerComboboxProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    return q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  }, [value, options]);

  // Hide when there's nothing useful left to pick — empty roster, no
  // match, or the lone match already equals what's typed.
  const sole = matches.length === 1 && matches[0]!.toLowerCase() === value.trim().toLowerCase();
  const visible = open && matches.length > 0 && !sole;

  useEffect(() => {
    if (!visible) setActive(-1);
  }, [visible]);

  function choose(name: string): void {
    onChange(name);
    setOpen(false);
    setActive(-1);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "ArrowDown") {
      if (!visible) {
        if (matches.length > 0) setOpen(true);
        return;
      }
      e.preventDefault();
      setActive((a) => (a >= matches.length - 1 ? 0 : a + 1));
    } else if (e.key === "ArrowUp") {
      if (!visible) return;
      e.preventDefault();
      setActive((a) => (a <= 0 ? matches.length - 1 : a - 1));
    } else if (e.key === "Enter") {
      if (visible && active >= 0 && active < matches.length) {
        e.preventDefault();
        choose(matches[active]!);
      }
    }
    // Escape is left to Radix: when the list is open its popover is the
    // top dismissable layer, so Escape closes the list and stops there
    // (the modal stays open); when the list is closed, Escape falls
    // through to the Dialog and closes it — matching the native
    // datalist's behaviour and every other field in this modal.
  }

  return (
    <PopoverPrimitive.Root
      open={visible}
      onOpenChange={(next) => {
        // Radix asks to close on outside interaction; opening is driven
        // by focus/typing, so only honour the close.
        if (!next) setOpen(false);
      }}
    >
      <PopoverPrimitive.Anchor asChild>
        <input
          type="text"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={visible}
          aria-controls={visible ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          autoComplete="off"
          value={value}
          placeholder={placeholder}
          className={cn(FIELD_CLASS, className)}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={handleKeyDown}
        />
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className="log-pop log-combo-content"
          // Keep the caret in the field — the list is for pointing at,
          // not for focusing into (keyboard nav runs through the input).
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div id={listId} role="listbox" className="log-combo-list">
            {matches.map((name, i) => (
              <button
                key={name}
                id={`${listId}-${i}`}
                type="button"
                role="option"
                aria-selected={i === active || undefined}
                title={name}
                className={cn("log-combo-item", i === active && "is-active")}
                onMouseEnter={() => setActive(i)}
                // Prevent the field blur that would otherwise close the
                // list before the click lands.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(name)}
              >
                {name}
              </button>
            ))}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
